import { Link } from "react-router-dom";
import { CheckCircle2, MapPin } from "lucide-react";

/**
 * Week view — 7 columns × N hour rows. Each event is anchored at its
 * exact start time (rounded to 30min slots) and sized proportionally
 * to a fixed 45-minute default (RTs at Caring average ~45min).
 *
 * Props:
 *   weekStart: JS Date at the Monday of the week to render
 *   events:    array of calendar events (already filtered)
 *   onCellClick(iso): optional handler when clicking an empty slot
 */
const DAY_LABELS_PT = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const HOUR_START = 8;   // 08:00
const HOUR_END = 21;    // 21:00 (exclusive)
const SLOT_MIN = 30;    // 30-min slots
const SLOT_HEIGHT = 26; // px per 30-min slot

function pad(n) { return String(n).padStart(2, "0"); }
function ymd(d) { return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`; }
function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }

function parseIsoLocal(iso) {
  if (!iso) return null;
  // Server ISOs may or may not have TZ; we treat them as local Europe/Lisbon.
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  return { y: +m[1], mo: +m[2], da: +m[3], h: +m[4], mi: +m[5] };
}

function minutesFromMidnight(iso) {
  const p = parseIsoLocal(iso);
  if (!p) return null;
  return p.h * 60 + p.mi;
}

export default function WeekView({ weekStart, events, todayIso, highlightIso }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const dayIsos = days.map(ymd);
  const slots = [];
  for (let h = HOUR_START; h < HOUR_END; h++) {
    slots.push({ h, m: 0 });
    slots.push({ h, m: 30 });
  }
  const totalMinutes = (HOUR_END - HOUR_START) * 60;
  const totalHeight = (totalMinutes / SLOT_MIN) * SLOT_HEIGHT;

  const eventsByDay = {};
  for (const iso of dayIsos) eventsByDay[iso] = [];
  for (const ev of events) {
    const day = (ev.scheduled_qt_at || "").slice(0, 10);
    if (eventsByDay[day]) eventsByDay[day].push(ev);
  }

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="week-view">
      {/* Header row */}
      <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-border">
        <div className="border-r border-border" />
        {days.map((d, i) => {
          const iso = dayIsos[i];
          const isToday = iso === todayIso;
          const isHighlight = iso === highlightIso;
          const cnt = eventsByDay[iso].length;
          return (
            <div key={iso} className={`text-center py-2 border-r border-border last:border-r-0 ${isHighlight ? "bg-[hsl(var(--success-bg))]" : isToday ? "bg-primary/8" : ""}`}>
              <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${isHighlight ? "text-[hsl(var(--success))]" : "text-muted-foreground"}`}>{DAY_LABELS_PT[i]}</div>
              <div className={`text-lg font-semibold mt-0.5 ${isToday ? "text-primary" : isHighlight ? "text-[hsl(var(--success))]" : "text-foreground"}`}>
                {d.getDate()}
                {cnt > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] rounded-full bg-primary/15 text-primary font-mono px-1.5">
                    {cnt}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Slot grid */}
      <div className="grid grid-cols-[60px_repeat(7,1fr)] relative" style={{ height: `${totalHeight}px` }}>
        {/* Time labels */}
        <div className="border-r border-border">
          {slots.map((s, idx) => (
            <div
              key={idx}
              className="text-[10px] text-muted-foreground pr-2 text-right border-b border-border/40"
              style={{ height: `${SLOT_HEIGHT}px`, lineHeight: `${SLOT_HEIGHT}px` }}
            >
              {s.m === 0 ? `${pad(s.h)}:00` : ""}
            </div>
          ))}
        </div>

        {/* 7 day columns */}
        {dayIsos.map((iso) => {
          const isToday = iso === todayIso;
          return (
            <div
              key={iso}
              className={`relative border-r border-border last:border-r-0 ${isToday ? "bg-primary/5" : ""}`}
              data-testid={`week-day-${iso}`}
            >
              {slots.map((s, idx) => (
                <div
                  key={idx}
                  className={`border-b ${s.m === 0 ? "border-border/40" : "border-transparent"}`}
                  style={{ height: `${SLOT_HEIGHT}px` }}
                />
              ))}
              {eventsByDay[iso].map((ev) => {
                const mm = minutesFromMidnight(ev.scheduled_qt_at);
                if (mm == null) return null;
                const startMin = HOUR_START * 60;
                const offset = Math.max(0, Math.round((mm - startMin) / SLOT_MIN) * SLOT_MIN);
                const top = (offset / SLOT_MIN) * SLOT_HEIGHT;
                // Default RT duration: 45min. Height ~ 1.5 slots (39px).
                const height = SLOT_HEIGHT * 1.5;
                return (
                  <Link
                    key={ev.id}
                    to={`/leads/${ev.id}`}
                    data-testid={`week-event-${ev.id}`}
                    className="absolute left-1 right-1 rounded-md border p-1.5 overflow-hidden hover:brightness-110 shadow-sm"
                    style={{
                      top: `${top}px`,
                      height: `${height}px`,
                      backgroundColor: ev.consultor_color + "22",
                      borderColor: ev.consultor_color + "80",
                      color: ev.consultor_color,
                    }}
                    title={`${ev.name} · ${ev.consultor_name || ""} · ${ev.district || ""}`}
                  >
                    <div className="flex items-center gap-1 text-[10px] font-mono opacity-80">
                      {parseIsoLocal(ev.scheduled_qt_at)?.h.toString().padStart(2, "0")}
                      :
                      {parseIsoLocal(ev.scheduled_qt_at)?.mi.toString().padStart(2, "0")}
                      {ev.completed && <CheckCircle2 className="w-2.5 h-2.5" />}
                    </div>
                    <div className="text-[11px] font-semibold truncate leading-tight">{ev.name}</div>
                  </Link>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { addDays, ymd };
